/**
 * Platform-specific reading of an import file into the shared ImportSheet shape.
 *
 * Kept separate from importFileParser so the parsing logic stays unit-testable without
 * pulling expo-file-system (and its native module) into the jest environment.
 */

import * as FileSystem from 'expo-file-system'
import {
  type ImportSheet,
  importFileKindFromName,
  MalformedImportFileError,
  parseCsvText,
  parseTsvText,
  parseWorkbookData,
} from './importFileParser'

export { MalformedImportFileError }

export class UnsupportedImportFileError extends Error {
  constructor(public fileName: string) {
    super(`Unsupported import file: ${fileName}`)
    this.name = 'UnsupportedImportFileError'
  }
}

/** Read a File handed over by the web dropzone / file input. */
export const readImportFileFromWeb = async (file: File): Promise<ImportSheet> => {
  const kind = importFileKindFromName(file.name)
  if (!kind) throw new UnsupportedImportFileError(file.name)

  if (kind === 'csv') {
    return parseCsvText(await file.text())
  }
  if (kind === 'tsv') {
    return parseTsvText(await file.text())
  }
  return parseWorkbookData(await file.arrayBuffer(), 'array')
}

/**
 * Read a file picked with expo-document-picker on native.
 *
 * Binary workbooks are read as base64 rather than through fetch(): a fetch of a file://
 * URI cannot be relied on to produce an ArrayBuffer on React Native.
 */
export const readImportFileFromUri = async (uri: string, fileName: string): Promise<ImportSheet> => {
  const kind = importFileKindFromName(fileName)
  if (!kind) throw new UnsupportedImportFileError(fileName)

  if (kind === 'csv') {
    return parseCsvText(await FileSystem.readAsStringAsync(uri))
  }
  if (kind === 'tsv') {
    return parseTsvText(await FileSystem.readAsStringAsync(uri))
  }
  const base64 = await FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  })
  return parseWorkbookData(base64, 'base64')
}

/** MIME types + extensions accepted by the document picker on native. */
export const IMPORT_DOCUMENT_PICKER_TYPES = [
  'text/csv',
  'text/comma-separated-values',
  'text/tab-separated-values',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

/** `accept` map for react-dropzone on web. */
export const IMPORT_DROPZONE_ACCEPT = {
  'text/csv': ['.csv'],
  'text/tab-separated-values': ['.tsv'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}
